import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react'

export interface BaiduMapProps {
    center: { lat: number; lng: number }
    zoom?: number
    style?: React.CSSProperties
    className?: string
    options?: {
        zoomControl?: boolean
        streetViewControl?: boolean
        fullscreenControl?: boolean
        mapTypeControl?: boolean
    }
    onLoad?: (map: any) => void
    onZoomChanged?: () => void
    onCenterChanged?: () => void
    onIdle?: () => void
    children?: React.ReactNode
}

export interface BaiduMapRef {
    getMap: () => any
}

const BaiduMapContext = React.createContext<any>(null)

export const useBaiduMap = () => React.useContext(BaiduMapContext)

const BaiduMap = forwardRef<BaiduMapRef, BaiduMapProps>(({
    center,
    zoom = 15,
    style,
    className,
    options,
    onLoad,
    onZoomChanged,
    onCenterChanged,
    onIdle,
    children
}, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<any>(null)
    const [mapReady, setMapReady] = useState(false)
    const isInitialized = useRef(false)

    useImperativeHandle(ref, () => ({
        getMap: () => mapRef.current
    }))

    const handleZoomChanged = useCallback(() => {
        onZoomChanged?.()
    }, [onZoomChanged])

    const handleCenterChanged = useCallback(() => {
        onCenterChanged?.()
    }, [onCenterChanged])

    const handleIdle = useCallback(() => {
        onIdle?.()
    }, [onIdle])

    useEffect(() => {
        if (!containerRef.current || !window.BMapGL || isInitialized.current) return

        const map = new window.BMapGL.Map(containerRef.current, {
            enableMapClick: true,
        })

        const point = new window.BMapGL.Point(center.lng, center.lat)
        map.centerAndZoom(point, zoom)
        map.enableScrollWheelZoom(true)

        // 컨트롤 추가
        if (options?.zoomControl !== false) {
            map.addControl(new window.BMapGL.ZoomControl())
        }

        mapRef.current = map
        isInitialized.current = true
        setMapReady(true)
        onLoad?.(map)

        map.addEventListener('zoomend', handleZoomChanged)
        map.addEventListener('moveend', handleCenterChanged)
        map.addEventListener('tilesloaded', handleIdle)

        return () => {
            map.removeEventListener('zoomend', handleZoomChanged)
            map.removeEventListener('moveend', handleCenterChanged)
            map.removeEventListener('tilesloaded', handleIdle)
        }
    }, [])

    // center 변경 시 panTo
    useEffect(() => {
        if (!mapRef.current || !isInitialized.current) return
        const point = new window.BMapGL.Point(center.lng, center.lat)
        mapRef.current.panTo(point)
    }, [center.lat, center.lng])

    return (
        <div className={className} style={{ width: '100%', height: '100%', ...style }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {mapReady && mapRef.current && (
                <BaiduMapContext.Provider value={mapRef.current}>
                    {children}
                </BaiduMapContext.Provider>
            )}
        </div>
    )
})

export default BaiduMap
export { BaiduMapContext }
